import React, { useEffect, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";

const emptyForm = {
  name: "", hsn: "", category: "", brand: "", unit: "PCS",
  purchase_price: 0, sale_price: 0, tax_rate: 18,
  opening_stock: 0, current_stock: 0, reorder_level: 0,
};

export default function Products() {
  const { activeCompanyId, activeStoreId } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products${q ? `?q=${encodeURIComponent(q)}` : ""}`);
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId, q]);

  const startAdd = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const startEdit = (r) => {
    setEditing(r); setForm({ ...emptyForm, ...r }); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!editing) payload.current_stock = payload.opening_stock;
      if (editing) {
        await api.patch(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="page-shell space-y-6" id="report-print-root">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">Inventory</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Products</h1>
        </div>
        <div className="flex items-center gap-3">
          <PageExportBar
            title="Products"
            filename={`products-${new Date().toISOString().slice(0,10)}`}
            columns={[
              { key: "name", label: "Name" },
              { key: "hsn", label: "HSN" },
              { key: "category", label: "Category" },
              { key: "unit", label: "Unit" },
              { key: "purchase_price", label: "Purchase" },
              { key: "sale_price", label: "Sale" },
              { key: "tax_rate", label: "GST %" },
              { key: "current_stock", label: "Stock" },
              { key: "reorder_level", label: "Reorder" },
            ]}
            rows={rows}
            testidPrefix="products"
          />
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…"
              className="pl-9 h-9 w-64" data-testid="prod-search" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={startAdd} className="h-9" data-testid="prod-add-btn">
                <Plus size={15} weight="bold" className="mr-1.5" /> New Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading tracking-tight">{editing ? "Edit Product" : "New Product"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-3 gap-4">
                <div className="col-span-3 space-y-1.5">
                  <Label className="eyebrow">Product Name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">HSN / SAC</Label>
                  <Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Category</Label>
                  <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Brand</Label>
                  <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Unit</Label>
                  <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Purchase Price (₹)</Label>
                  <Input type="number" step="0.01" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: parseFloat(e.target.value) || 0 })} className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Sale Price (₹)</Label>
                  <Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: parseFloat(e.target.value) || 0 })} className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Tax Rate (%)</Label>
                  <Input type="number" step="0.01" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Opening Stock</Label>
                  <Input type="number" step="0.01" value={form.opening_stock} onChange={(e) => setForm({ ...form, opening_stock: parseFloat(e.target.value) || 0 })} className="font-mono" disabled={!!editing} />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Current Stock</Label>
                  <Input type="number" step="0.01" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: parseFloat(e.target.value) || 0 })} className="font-mono" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Reorder Level</Label>
                  <Input type="number" step="0.01" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: parseFloat(e.target.value) || 0 })} className="font-mono" />
                </div>
                <DialogFooter className="col-span-3 mt-2">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="prod-save">{editing ? "Update" : "Create"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>HSN</th>
              <th>Category</th>
              <th className="num">Purchase</th>
              <th className="num">Sale</th>
              <th className="num">Tax %</th>
              <th className="num">Stock</th>
              <th className="num" style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-muted-foreground py-10">
                No products yet. Add products to start selling.
              </td></tr>
            ) : rows.map((r) => {
              const low = (r.reorder_level || 0) > 0 && (r.current_stock || 0) <= r.reorder_level;
              return (
                <tr key={r.id} data-testid={`prod-row-${r.id}`}>
                  <td>
                    <div className="font-medium">{r.name}</div>
                  </td>
                  <td className="font-mono text-[12.5px]">{r.hsn || "—"}</td>
                  <td>{r.category || "—"}</td>
                  <td className="num">{inr(r.purchase_price)}</td>
                  <td className="num">{inr(r.sale_price)}</td>
                  <td className="num">{Number(r.tax_rate || 0).toFixed(0)}%</td>
                  <td className="num">
                    <span className={low ? "text-warning" : ""}>
                      {low && <Warning size={12} weight="fill" className="inline mr-1 text-warning" style={{ color: "hsl(var(--warning))" }} />}
                      {Number(r.current_stock || 0).toFixed(2)} {r.unit}
                    </span>
                  </td>
                  <td className="num" style={{ textAlign: "right" }}>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(r)} data-testid={`prod-edit-${r.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r.id)} data-testid={`prod-del-${r.id}`}>
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
